import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AbstractControl, AsyncValidatorFn } from '@angular/forms';
import { EventSummary } from "@cms/shared-library";
import { map, Observable, of } from 'rxjs';
import { CertificationPersonDTO } from 'src/app/models/certification-person-dto';
import { ClassCourseWithEnrolmentsDTO } from 'src/app/models/class-coursewithenrolments-dto.model';
import { BulkCourseEnrolmentStatusUpdate, ClassEnrolment } from 'src/app/models/class-enrolment.model';
import { CourseDTO } from 'src/app/models/course-dto.model';
import { CourseSchedule } from 'src/app/models/course-schedule';
import { CourseSearchForm } from 'src/app/models/course-search-form.model';
import { CourseTrainingProgramDetails } from 'src/app/models/course-training-program-details.model';
import { NextScheduledCoursesModel } from 'src/app/models/next-scheduled-courses.model';
import { environment } from 'src/environments/environment';
import { CourseMapper } from '../../models/mappers/courser.mapper';
import { CourseInterface } from './course.interface';


@Injectable({ providedIn: 'root' })
export class CourseService implements CourseInterface {

  constructor(private httpClient: HttpClient) { }

  SearchCoursesCount(courseSearchForm: CourseSearchForm, isPublicSearch: boolean = false): Observable<number> {
    return this.httpClient.post<any>(environment.apiBaseUrl + (isPublicSearch ? `course/GetCourseSearchResultsForPublicCount` : `course/GetCourseSearchResultsCount`), courseSearchForm);
  }

  SearchCourses(courseSearchForm: CourseSearchForm, isPublicSearch: boolean = false): Observable<CourseSchedule[]> {
    return this.httpClient.post<any>(environment.apiBaseUrl + (isPublicSearch ? `course/GetCourseSearchResultsForPublic` : `course/GetCourseSearchResults`), courseSearchForm).pipe(map(this.courseMapper));
  }

  GetNextCourseScheduled(courseId: string): Observable<NextScheduledCoursesModel> {
    return this.httpClient.get<NextScheduledCoursesModel>(environment.apiBaseUrl + `course/GetNextScheduledCourses/${courseId}`);
  }

  GetTimelineEventsByCourseApprovalId(courseApprovalId: string | null): Observable<EventSummary[]> {
    return this.httpClient.get<EventSummary[]>(environment.apiBaseUrl + `course/GetTimelineEventsByCourseApprovalId/${courseApprovalId}`);
  }

  PublishCourse(courseDTO: CourseDTO): Observable<any> {
    return this.httpClient.post<any>(environment.apiBaseUrl + `course/PublishCourse/`, courseDTO);
  }

  UpdateCourse(courseDTO: CourseDTO): Observable<any> {
    return this.httpClient.post<any>(environment.apiBaseUrl + `course/UpdateCourse/`, courseDTO);
  }
  CancelCourse(courseDTO: CourseDTO, status: Number): Observable<any> {
    return this.httpClient.post<any>(environment.apiBaseUrl + `course/UpdateCourse/${status}`, courseDTO);
  }

  GetPublishedCourses(caId: string): Observable<CourseDTO[]> {
    return this.httpClient.get<CourseDTO[]>(environment.apiBaseUrl + `Course/GetPublishedCourses/${caId}`);
  }

  GetCourseDetails(courseId: string): Observable<CourseTrainingProgramDetails> {
    return this.httpClient.get<CourseTrainingProgramDetails>(environment.apiBaseUrl + `Course/GetCourseDetails/${courseId}`);
  }

  private courseMapper = (course: any[]) => { return CourseMapper.map(course); };

  GetCourseByCourseId(courseId: string): Observable<CourseDTO> {
    return this.httpClient.get<CourseDTO>(environment.apiBaseUrl + `course/GetCourseByCourseId/${courseId}`);
  }

  SaveCourseEnrolments(enrollments: ClassEnrolment[]): Observable<string> {
    return this.httpClient.post<string>(environment.apiBaseUrl + `course/SaveCourseEnrolments`, enrollments);
  }

  SaveCourseEnrolmentsForPublic(enrollments: ClassEnrolment[]): Observable<string> {
    return this.httpClient.post<string>(environment.apiBaseUrl + `course/SaveCourseEnrolmentsForPublic`, enrollments);
  }

  CompleteCourseEnrolments(enrollments: ClassEnrolment[]): Observable<Boolean> {
    return this.httpClient.post<Boolean>(environment.apiBaseUrl + `course/CompleteCourseEnrolments`, enrollments);
  }

  GetEnrolmentDetails(registrationId: string): Observable<ClassEnrolment> {
    return this.httpClient.get<ClassEnrolment>(environment.apiBaseUrl + `course/GetEnrolmentDetails/${registrationId}`);
  }

  GetEnrolmentDetailsByGroupIdOrRegistrationId(groupOrRegId: string): Observable<ClassCourseWithEnrolmentsDTO> {
    return this.httpClient.get<ClassCourseWithEnrolmentsDTO>(environment.apiBaseUrl + `course/GetEnrolmentDetailsByGroupIdOrRegistrationId/${groupOrRegId}`);
  }
  GetInstructors(): Observable<CertificationPersonDTO[]> {
    return this.httpClient.get<CertificationPersonDTO[]>(environment.apiBaseUrl + 'course/getInstructors');
  }

  IsEmailExistInCourse(enrollment: ClassEnrolment): Observable<boolean> {
    return this.httpClient.post<boolean>(environment.apiBaseUrl + `course/IsEmailExistInCourse`, enrollment, { reportProgress: true });
  }

  UpdateEnrolmentDetailsInBulkByGroupIdOrRegistrationId(enrollmentRequest: BulkCourseEnrolmentStatusUpdate): Observable<Boolean> {
    return this.httpClient.post<Boolean>(environment.apiBaseUrl + `course/UpdateEnrolmentDetailsInBulkByGroupIdOrRegistrationId`, enrollmentRequest);
  }

  SendRecordOfCompletion(registrationIds: string[]): Observable<any> {
    return this.httpClient.post<Boolean>(environment.apiBaseUrl + `course/SendRecordOfCompletion`, { registrationIds: registrationIds });
  }

  DownloadRecordOfCompletion(registrationIds: string[]): Observable<any>{
    return this.httpClient.post<Boolean>(environment.apiBaseUrl + `course/DownloadRecordOfCompletion`, { registrationIds: registrationIds });
  }

  GetCourseWithdrawalReason(courseApprovalId: string): Observable<any> {
    return this.httpClient.get<any>(environment.apiBaseUrl + `course/GetCourseWithdrawalReason/${courseApprovalId}`);
  }

  CheckIfLearnerInfoMatches(enrollments: ClassEnrolment[]): Observable<{infoMatches: boolean, conflictingEmails: string[]}> {
    return this.httpClient.post<{infoMatches: boolean, conflictingEmails: string[]}>(environment.apiBaseUrl + `course/CheckIfLearnerInfoMatches`, enrollments);
  }
}

export function uniqueEmailValidator(courseService: CourseService, courseId: string, origVal?: string): AsyncValidatorFn {
  return (control: AbstractControl) => {
    if (origVal && origVal == control.value) return of(null)
    if (control.value == null || control.value == undefined || control.value == '') return of(null)
    let enrolmentDto = {
      CourseId: courseId,
      EmailAddress: control.value,
      Phone: '',
      FirstName: '',
      LastName: ''
    };

    return courseService.IsEmailExistInCourse(enrolmentDto).pipe(map(doesExist => {
      return doesExist ? { exists: true } : null;
    }))
  }
}
